import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Building2, Users, CheckSquare, Mail, Crown, FileUp, BarChart3, Pencil } from "lucide-react";
import { OverviewTab } from "./tabs/OverviewTab";
import { UsersTab } from "./tabs/UsersTab";
import { ApprovalsTab } from "./tabs/ApprovalsTab";
import { AnalyticsTab } from "./tabs/AnalyticsTab";
import { LeadersTab } from "./tabs/LeadersTab";
import { ContentTab } from "./tabs/ContentTab";
import { ImportTab } from "./tabs/ImportTab";
import { EmailTab } from "./tabs/EmailTab";

const TABS_ALL = [
  { key: "overview", label: "Organizations", icon: Building2, Comp: OverviewTab },
  { key: "users", label: "Users", icon: Users, Comp: UsersTab },
  { key: "approvals", label: "Approvals", icon: CheckSquare, Comp: ApprovalsTab },
  { key: "analytics", label: "Analytics", icon: BarChart3, Comp: AnalyticsTab },
  { key: "leaders", label: "Org Leaders", icon: Crown, Comp: LeadersTab, adminOnly: true },
  { key: "content", label: "Content", icon: Pencil, Comp: ContentTab, adminOnly: true },
  { key: "import", label: "Import Class", icon: FileUp, Comp: ImportTab, adminOnly: true },
  { key: "email", label: "Email", icon: Mail, Comp: EmailTab },
];

function TabBar({ tabs, tab, setTab }) {
  return (
    <div className="flex flex-wrap gap-2 mb-7">
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink text-sm font-bold press ${active ? "shadow-pop-sm" : "bg-white"}`}
            style={active ? { background: "#0F1116", color: "#FFFDF5" } : {}}
          >
            <Icon size={15} /> {t.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("overview");
  const tabs = TABS_ALL.filter((t) => !t.adminOnly || isAdmin);
  const Active = tabs.find((t) => t.key === tab)?.Comp;
  const nav = isAdmin
    ? [{ to: "/admin", label: "Dashboard" }, { to: "/mentor", label: "Mentor View" }]
    : [{ to: "/admin", label: "Dashboard" }];

  return (
    <AppShell nav={nav}>
      <div className="animate-rise">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-4xl font-extrabold">{isAdmin ? "Admin Panel" : "Organization Panel"}</h1>
          <span className="text-xs font-bold px-3 py-1 rounded-full border-2 border-ink" style={{ background: isAdmin ? "#FF5722" : "#31B8FF", color: isAdmin ? "#FFFDF5" : "#0F1116" }}>
            {isAdmin ? "All organizations" : user?.organization_name}
          </span>
        </div>
        <p className="text-muted-foreground mb-6">
          {isAdmin ? "Manage every organization, user, and approval." : "Manage your organization's students, mentors, and approvals."}
        </p>
        <TabBar tabs={tabs} tab={tab} setTab={setTab} />
        {Active && <Active />}
      </div>
    </AppShell>
  );
}
